import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";

export type GiftType = "value" | "package";

export interface Gift {
  code: string;
  type: GiftType;
  value?: number;
  serviceId?: string;
  serviceName?: string;
  recipientPhone?: string;
  pricePaid?: number;
  expiryDate?: string;
  status?: string;
}

export interface PurchaseGiftParams {
  type: GiftType;
  value?: number;
  serviceId?: string;
  serviceIds?: { serviceId: string; vehicleType: string }[];
  recipientPhone?: string;
  recipientName?: string;
  deliveryMethod?: "share" | "sms";
  paymentMethod: "wallet" | "knet" | "credit_card" | "apple_pay";
}

/** Gateway purchases return this instead of a Gift (code revealed after payment). */
export interface PurchaseGatewayResponse {
  paymentRequired: true;
  paymentLink: string;
  giftId: string;
}

export type PurchaseGiftResponse = Gift | PurchaseGatewayResponse;

export interface ValidateGiftResult {
  valid: boolean;
  code: string;
  type: GiftType;
  value?: number;
  serviceId?: string;
  serviceName?: string;
  vehicleType?: string;
  expiryDate?: string;
}

export interface RedeemGiftResult {
  success: boolean;
  message?: string;
  type: GiftType;
  value?: number;
  serviceId?: string;
  serviceName?: string;
  giftId?: string;
}

/**
 * Purchase a gift code. Returns the created gift document (with `code`).
 * Wallet balance is invalidated on success so the deducted amount refreshes.
 */
export function usePurchaseGift() {
  const queryClient = useQueryClient();

  return useMutation<PurchaseGiftResponse, Error, PurchaseGiftParams>({
    mutationFn: async (params): Promise<PurchaseGiftResponse> => {
      const res = await apiClient.post("/gifts/purchase", params);
      if (!res.ok) {
        let message = "Failed to purchase gift";
        try {
          const data = await res.json();
          message = data?.message || message;
        } catch {
          /* ignore parse errors */
        }
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/wallets/balance"] });
    },
  });
}

/**
 * Validate a gift code (GET /gifts/validate/:code).
 * Enabled only when a code is provided.
 */
export function useValidateGift(code?: string) {
  return useQuery<ValidateGiftResult, Error>({
    queryKey: ["/gifts/validate", code],
    queryFn: async (): Promise<ValidateGiftResult> => {
      const res = await apiClient.get(`/gifts/validate/${encodeURIComponent(code || "")}`);
      if (!res.ok) {
        let message = "Invalid gift code";
        try {
          const data = await res.json();
          message = data?.message || message;
        } catch {
          /* ignore parse errors */
        }
        throw new Error(message);
      }
      return res.json();
    },
    enabled: !!code && code.trim().length > 0,
    retry: false,
    staleTime: 0,
  });
}

/**
 * Redeem a gift code. Wallet balance is invalidated on success since
 * value-type gifts credit the wallet.
 */
export function useRedeemGift() {
  const queryClient = useQueryClient();

  return useMutation<RedeemGiftResult, Error, { code: string; asCredit?: boolean }>({
    mutationFn: async ({ code, asCredit }): Promise<RedeemGiftResult> => {
      const res = await apiClient.post("/gifts/redeem", { code, asCredit });
      if (!res.ok) {
        let message = "Failed to redeem gift";
        try {
          const data = await res.json();
          message = data?.message || message;
        } catch {
          /* ignore parse errors */
        }
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/wallets/balance"] });
    },
  });
}
